import type { ColumnPinningState, VisibilityState } from "@tanstack/react-table";
import * as React from "react";

import type { DataTableColumnDef, DataTableProps } from "./data-table-types";
import { useColumnReorder } from "./data-table-reorder";
import { useColumnResize } from "./data-table-resize";
import {
  STRUCTURAL_COLUMN_IDS,
  TABLE_TOTAL_WIDTH_VAR,
  columnLeftVar,
  columnRightVar,
  columnWidthVar,
  getColumnDefId,
  getColumnLabel,
  mergeColumnOrder,
  resolveColumnBaseWidth,
  resolveColumnWidthLimits,
  resolveColumnWidths,
} from "./data-table-utils";

/**
 * The reference header fade: the left-pinned lane's LAST header cell paints
 * its fill as a gradient that dissolves at the trailing edge, so a column
 * label panning underneath ghosts out through that span instead of
 * hard-cutting at the cell boundary. Opaque across the leading 80% — the
 * pinned column's own title always sits on solid surface — and gone by the
 * edge.
 *
 * It is a background IMAGE on the cell rather than an overlay child, so it
 * paints beneath the title by definition and needs no z-index reasoning; a
 * positioned overlay would paint ABOVE its in-flow siblings whatever the DOM
 * order. `--table-fill` is the surface as a value, so this tracks the `surface`
 * prop and the theme with nothing to keep in sync.
 *
 * HEADER ROW ONLY on the left: body cells there keep the hard cut, because a
 * gradient fill would have to composite against the row's own wash (see the
 * pinned trailing-edge note below). The right lane's body cells are the one
 * exception, and BODY_EDGE_FADE_CLASS says why it holds there.
 */
export const HEADER_FADE_GRADIENT =
  "linear-gradient(to right, var(--table-fill) 0%, var(--table-fill) 80%, transparent 100%)";

const PINNED_LANE_TRAILING_RESERVE = 64;
const PINNED_LANE_MIN_COLUMN_WIDTH = 120;

/**
 * Below this container width the lane cap flips priority: instead of the lane
 * taking everything but a {@link PINNED_LANE_TRAILING_RESERVE} sliver, the
 * scrolling side keeps half the scrollport. On a 390px phone the old cap left
 * a 64px slit for every unpinned column — the name column read as the whole
 * table and each value panned past as a fragment. Splitting the port keeps a
 * full data column legible beside the name. The same threshold arms column
 * scroll-snap on the container (see the scroll container's class), so a pan
 * settles with a whole column framed in that half rather than two slivers.
 * Wider containers are untouched — desktop and tablet keep the sliver cap.
 */
export const NARROW_CONTAINER_WIDTH = 480;
const NARROW_PINNED_LANE_FRACTION = 0.5;

export function capPinnedLane(
  widths: Map<string, number>,
  leftIds: readonly string[],
  containerWidth: number,
): void {
  const lastId = leftIds.at(-1);
  if (containerWidth <= 0 || lastId === undefined) return;
  // Every pinned id is a resolved column, so each has a width.
  let lane = 0;
  for (const id of leftIds) lane += widths.get(id)!;
  // A sliver of the scrolling lane stays visible, so the pinned block never
  // reads as the whole table; on a narrow container the "sliver" becomes an
  // even split (see NARROW_CONTAINER_WIDTH).
  const cap =
    containerWidth < NARROW_CONTAINER_WIDTH
      ? Math.round(containerWidth * NARROW_PINNED_LANE_FRACTION)
      : containerWidth - PINNED_LANE_TRAILING_RESERVE;
  if (lane <= cap) return;
  widths.set(
    lastId,
    Math.max(PINNED_LANE_MIN_COLUMN_WIDTH, widths.get(lastId)! - (lane - cap)),
  );
}

const EMPTY_PINNED_COLUMN_IDS: string[] = [];
const RIGHT_PINNED_ACTIONS = ["__actions__"];
const EMPTY_COLUMN_VISIBILITY: VisibilityState = {};
const EMPTY_COLUMN_SIZING: Record<string, number> = {};

/**
 * The table's column layout: the controlled-or-internal order, visibility and
 * sizing state, the resolved pixel widths (the single source of truth for
 * layout, pushed into CSS variables on the scroll container), the per-column
 * cell styles, and the resize and reorder drag controllers over them.
 */
/**
 * Calls `update` when the scroll box or any of its direct children resizes,
 * including children that mount after the first run: a group section that
 * lands with its rows changes the content height while the box itself keeps
 * its size, so watching only the first children would miss it.
 */
function observeBoxAndChildren(el: HTMLElement, update: () => void): () => void {
  const resize = new ResizeObserver(update);
  resize.observe(el);
  for (const child of el.children) resize.observe(child);
  const mutation = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element) resize.observe(node);
      }
    }
    update();
  });
  mutation.observe(el, { childList: true });
  return () => {
    mutation.disconnect();
    resize.disconnect();
  };
}

export function useDataTableLayout<TData>({
  columns,
  columnsProp,
  columnOrderProp,
  onColumnOrderChange,
  columnVisibilityProp,
  onColumnVisibilityChange,
  addColumnHeader,
  columnSizingProp,
  onColumnSizingChange,
  pinnedColumnIds,
  fillColumnId,
  columnLayout,
  renderRowActions,
  containerWidth,
  scrollContainerRef,
  scrollFade,
  scrollMode,
  isTouch,
}: {
  /** The full column set, structural columns included. */
  columns: DataTableColumnDef<TData>[];
  /** The consumer's column set, in definition order. */
  columnsProp: DataTableColumnDef<TData>[];
  columnOrderProp: DataTableProps<TData>["columnOrder"];
  onColumnOrderChange: DataTableProps<TData>["onColumnOrderChange"];
  columnVisibilityProp: DataTableProps<TData>["columnVisibility"];
  onColumnVisibilityChange: DataTableProps<TData>["onColumnVisibilityChange"];
  addColumnHeader: DataTableProps<TData>["addColumnHeader"];
  columnSizingProp: DataTableProps<TData>["columnSizing"];
  onColumnSizingChange: DataTableProps<TData>["onColumnSizingChange"];
  pinnedColumnIds: string[];
  fillColumnId: DataTableProps<TData>["fillColumnId"];
  columnLayout: NonNullable<DataTableProps<TData>["columnLayout"]>;
  renderRowActions: DataTableProps<TData>["renderRowActions"];
  containerWidth: number;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  scrollFade: boolean;
  scrollMode: NonNullable<DataTableProps<TData>["scrollMode"]>;
  /** Drag affordances are pointer-shaped; a touch device gets none. */
  isTouch: boolean;
}) {
  // -------------------------------------------------------------------------
  // Column customization state (controlled props with internal fallbacks)
  // -------------------------------------------------------------------------
  //
  // Order and sizing have internal fallbacks because the table itself mutates
  // them (header drag, resize handles). Visibility is only ever changed by an
  // external settings surface, so it is read-only here.

  const [internalOrder, setInternalOrder] = React.useState<string[] | null>(
    null,
  );
  const [internalSizing, setInternalSizing] = React.useState<
    Record<string, number>
  >(EMPTY_COLUMN_SIZING);

  // Structural columns (`select`, `__actions__`) and pinned columns are never
  // hideable — the settings panel disables their switches, and this scrub
  // keeps a stale persisted payload (or a consumer mistake) from hiding them
  // anyway. TanStack honors `columnVisibility` state over a column's
  // `enableHiding: false`, so the entries must be dropped before reaching the
  // table. Uses the raw pinned prop (not the mobile-adjusted effective set)
  // so a phone and a desktop agree on what can be hidden.
  const visibility = React.useMemo<VisibilityState>(() => {
    const raw = columnVisibilityProp ?? EMPTY_COLUMN_VISIBILITY;
    let next: VisibilityState | null = null;
    for (const id of [...STRUCTURAL_COLUMN_IDS, ...pinnedColumnIds]) {
      if (id in raw) {
        next ??= { ...raw };
        delete next[id];
      }
    }
    return next ?? raw;
  }, [columnVisibilityProp, pinnedColumnIds]);
  const sizing = columnSizingProp ?? internalSizing;

  const handleOrderChange = React.useCallback(
    (next: string[]) => {
      if (onColumnOrderChange) onColumnOrderChange(next);
      else setInternalOrder(next);
    },
    [onColumnOrderChange],
  );

  const handleSizingChange = React.useCallback(
    (next: Record<string, number>) => {
      if (onColumnSizingChange) onColumnSizingChange(next);
      else setInternalSizing(next);
    },
    [onColumnSizingChange],
  );

  // Consumer column ids in definition order — the default display order.
  const defaultOrderIds = React.useMemo(
    () =>
      columnsProp
        .map((def) => getColumnDefId(def))
        .filter((id): id is string => id !== undefined),
    [columnsProp],
  );
  const userOrder = columnOrderProp ?? internalOrder ?? defaultOrderIds;

  // One merge rule for the whole feature: the settings panel renders from
  // mergeColumnOrder, so the grid must derive its order from the same merge —
  // otherwise a column added after the user persisted an order would appear
  // at different positions in the panel and the table.
  const mergedOrder = React.useMemo(
    () => mergeColumnOrder(userOrder, defaultOrderIds),
    [userOrder, defaultOrderIds],
  );

  // Structural columns bracket the consumer order. TanStack drops order ids
  // with no matching column, so this is safe when select/actions are absent.
  const tableColumnOrder = React.useMemo(
    () => ["select", ...mergedOrder, "__actions__"],
    [mergedOrder],
  );

  // `Required` so the two edges below are non-optional: both are always set
  // here, and every reader downstream indexes them without a guard.
  const columnPinning = React.useMemo<Required<ColumnPinningState>>(
    () => ({
      left: pinnedColumnIds,
      right:
        renderRowActions || onColumnVisibilityChange || addColumnHeader != null
          ? RIGHT_PINNED_ACTIONS
          : EMPTY_PINNED_COLUMN_IDS,
    }),
    [pinnedColumnIds, renderRowActions, onColumnVisibilityChange, addColumnHeader],
  );

  // -------------------------------------------------------------------------
  // Resolved pixel widths — the single source of truth for layout
  // -------------------------------------------------------------------------

  // Visible column defs partitioned into the on-screen order (left pinned →
  // center → right pinned), mirroring how TanStack partitions the group
  // tables' headers/cells.
  const visualLayout = React.useMemo(() => {
    const byId = new Map<string, DataTableColumnDef<TData>>();
    for (const def of columns) {
      const id = getColumnDefId(def);
      if (id !== undefined && visibility[id] !== false) byId.set(id, def);
    }
    // tableColumnOrder is a full merge of persisted + default ids, so every
    // visible column is guaranteed a slot — no append fallback needed (one
    // would re-introduce a second ordering rule alongside mergeColumnOrder).
    const orderedIds: string[] = [];
    const seen = new Set<string>();
    for (const id of tableColumnOrder) {
      if (byId.has(id) && !seen.has(id)) {
        orderedIds.push(id);
        seen.add(id);
      }
    }
    const leftIds = columnPinning.left.filter((id) => byId.has(id));
    const rightIds = columnPinning.right.filter((id) => byId.has(id));
    const pinnedSet = new Set([...leftIds, ...rightIds]);
    const centerIds = orderedIds.filter((id) => !pinnedSet.has(id));
    const visualIds = [...leftIds, ...centerIds, ...rightIds];
    return {
      visualIds,
      leftIds,
      rightIds,
      reorderableIds: centerIds.filter(
        (id) => !STRUCTURAL_COLUMN_IDS.has(id),
      ),
      resolvable: visualIds.map((id) => ({ id, meta: byId.get(id)?.meta })),
    };
  }, [columns, visibility, tableColumnOrder, columnPinning]);

  // The fill column absorbs any width deficit so the table always reaches the
  // container's right edge (the actions column stays flush). The consumer's
  // fillColumnId stays elastic across resizes of OTHER columns; only once the
  // user explicitly sizes it (or none is designated/visible) does the
  // visually last consumer column take over. The stretch is display-only —
  // `baseWidth` is always the UNSTRETCHED width (it is also what the resize
  // bake persists), so a container-dependent stretched value never leaks into
  // stored sizing and the resize drag's startStretch math stays exact even
  // when the fill column is the visually last one.
  // `resolveColumnWidths` sets an entry for every column it is handed and
  // never deletes one, so the resulting map is keyed by exactly
  // `visualLayout.visualIds` — every lookup below it is a guaranteed hit.
  //
  // `columnLayout: 'packed'` skips ALL of this: columns render at
  // their natural widths and pack left, the table edge lands wherever they
  // end, and no fill column exists (the resize drag then moves the edge
  // freely instead of gluing it to the container).
  const { resolvedWidths, fillColumn } = React.useMemo(() => {
    const widths = resolveColumnWidths(
      visualLayout.resolvable,
      sizing,
      containerWidth,
      fillColumnId,
      columnLayout,
    );
    capPinnedLane(widths, visualLayout.leftIds, containerWidth);
    if (columnLayout === "packed") {
      return { resolvedWidths: widths, fillColumn: null };
    }
    let fillId: string | null =
      fillColumnId !== undefined &&
      widths.has(fillColumnId) &&
      sizing[fillColumnId] === undefined
        ? fillColumnId
        : null;
    if (fillId === null) {
      for (let i = visualLayout.visualIds.length - 1; i >= 0; i--) {
        const id = visualLayout.visualIds[i];
        if (id === undefined || STRUCTURAL_COLUMN_IDS.has(id)) continue;
        fillId = id;
        break;
      }
    }
    if (fillId === null) {
      return { resolvedWidths: widths, fillColumn: null };
    }
    // Unstretched base. When resolveColumnWidths stretched fillColumnId
    // (pristine fill layout) the map already includes the stretch, so the
    // base is re-derived from the definition; in every other case the map
    // value IS the base (flex share or pixel width).
    const def = visualLayout.resolvable.find((c) => c.id === fillId);
    const baseWidth =
      fillId === fillColumnId && def !== undefined
        ? resolveColumnBaseWidth(def, sizing, containerWidth)
        : widths.get(fillId)!;
    let total = 0;
    for (const width of widths.values()) total += width;
    const totalAtBase = total - (widths.get(fillId)! - baseWidth);
    const deficit = Math.max(0, containerWidth - totalAtBase);
    widths.set(fillId, baseWidth + deficit);
    return { resolvedWidths: widths, fillColumn: { id: fillId, baseWidth } };
  }, [visualLayout, sizing, containerWidth, fillColumnId, columnLayout]);

  const totalResolvedWidth = React.useMemo(() => {
    let total = 0;
    for (const width of resolvedWidths.values()) total += width;
    return total;
  }, [resolvedWidths]);

  // Push widths + pinned offsets into CSS variables on the scroll container.
  // Cells reference the variables through stable style objects, so layout
  // changes repaint without re-rendering rows; an active resize drag writes
  // the same variables imperatively per frame and commits state on release.
  React.useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    for (const [id, width] of resolvedWidths) {
      el.style.setProperty(columnWidthVar(id), `${width}px`);
    }
    el.style.setProperty(TABLE_TOTAL_WIDTH_VAR, `${totalResolvedWidth}px`);
    let left = 0;
    for (const id of visualLayout.leftIds) {
      el.style.setProperty(columnLeftVar(id), `${left}px`);
      left += resolvedWidths.get(id)!;
    }
    // Column snap (narrow containers) aligns to the pinned lane's edge:
    // without this padding a snapped column parks under the sticky lane.
    el.style.setProperty("scroll-padding-left", `${left}px`);
    let right = 0;
    for (const id of [...visualLayout.rightIds].reverse()) {
      el.style.setProperty(columnRightVar(id), `${right}px`);
      right += resolvedWidths.get(id)!;
    }
  }, [resolvedWidths, totalResolvedWidth, visualLayout, scrollContainerRef]);

  // `data-dtlaneabut`: the right-pinned lane sits flush against the column
  // before it — no horizontal overflow, or scrolled to the end. The right
  // lane's header fade reads it to decide whether hovering that column may
  // lift the fade (HEADER_FADE_RIGHT_CLASS). An attribute toggled here, not
  // React state: it changes on every scroll tick and nothing else renders
  // from it. Width deps re-run it after a resize commits; the live drag
  // yields through `data-dtresizing` regardless.
  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const update = () => {
      const abuts = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      if (abuts) el.setAttribute("data-dtlaneabut", "true");
      else el.removeAttribute("data-dtlaneabut");
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    return () => el.removeEventListener("scroll", update);
  }, [totalResolvedWidth, containerWidth, scrollContainerRef]);

  // `data-has-overflow-y`: the rows run past the scroll box. The bottom
  // gutter's half-box slack reads it (the way the widget kit's gutter reads
  // the attribute Base UI's ScrollArea writes on its viewport), so a table
  // that fits keeps its resting foot and one that overflows gains the slack.
  // An attribute, not React state: it changes as rows land and nothing else
  // renders from it. Internal scroll only — a `fit` box never overflows.
  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || scrollMode !== "internal") return;
    const update = () => {
      if (el.scrollHeight > el.clientHeight) el.setAttribute("data-has-overflow-y", "true");
      else el.removeAttribute("data-has-overflow-y");
    };
    update();
    const stop = observeBoxAndChildren(el, update);
    return () => {
      stop();
      el.removeAttribute("data-has-overflow-y");
    };
  }, [scrollMode, scrollContainerRef]);

  // `scrollFade`: the rows dissolve at the scroll box's edges only while
  // rows continue past them. The mask reads two lengths — how far the
  // content runs past the top and past the bottom — written here as CSS
  // variables on every scroll tick and whenever the box or its content
  // resizes (rows landing, a group folding), the way Base UI's ScrollArea
  // publishes its own overflow lengths. Variables, not React state: they
  // change on every tick and nothing else renders from them.
  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !scrollFade || scrollMode !== "internal") return;
    const update = () => {
      const end = Math.max(0, el.scrollHeight - el.clientHeight - el.scrollTop);
      el.style.setProperty("--dt-overflow-start", `${Math.max(0, el.scrollTop)}px`);
      el.style.setProperty("--dt-overflow-end", `${end}px`);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const stop = observeBoxAndChildren(el, update);
    return () => {
      el.removeEventListener("scroll", update);
      stop();
      el.style.removeProperty("--dt-overflow-start");
      el.style.removeProperty("--dt-overflow-end");
    };
  }, [scrollFade, scrollMode, scrollContainerRef]);

  // Reveal scroll: a column toggled visible while the table is mounted (the
  // circle-plus picker, the settings panel, a view's Fields page — all of
  // which also append it to the order) can land past the right edge of the
  // scroll viewport, where the "add" is invisible. Exactly one added id is
  // the gesture's signature; bulk appearances (a saved-view switch swapping
  // the column set) don't scroll, and reorders/removals of existing ids
  // never match. The prev-set ref updates on EVERY run, so re-runs from
  // width or pinning changes diff as empty.
  const prevVisualIdsRef = React.useRef<readonly string[] | null>(null);
  React.useEffect(() => {
    const prev = prevVisualIdsRef.current;
    prevVisualIdsRef.current = visualLayout.visualIds;
    if (prev === null) return;
    const prevSet = new Set(prev);
    const added = visualLayout.visualIds.filter(
      (id) => !prevSet.has(id) && !STRUCTURAL_COLUMN_IDS.has(id),
    );
    if (added.length !== 1) return;
    const columnId = added[0]!;
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl) return;
    const cell = Array.from(
      scrollEl.querySelectorAll<HTMLElement>("[data-dtreorderid]"),
    ).find((el) => el.dataset["dtreorderid"] === columnId);
    // Pinned (always on screen) and unrendered headers have nothing to
    // reveal.
    if (!cell) return;
    const pad = 16;
    const cellRect = cell.getBoundingClientRect();
    const hostRect = scrollEl.getBoundingClientRect();
    // The pinned lanes overlay the viewport's edges — a cell under either is
    // as hidden as one scrolled out entirely. The right lane is the sticky
    // actions column (row menus / the circle-plus picker), which is exactly
    // where a shown-at-end column lands.
    let leftLane = 0;
    for (const id of visualLayout.leftIds) {
      leftLane += resolvedWidths.get(id)!;
    }
    let rightLane = 0;
    for (const id of visualLayout.rightIds) {
      rightLane += resolvedWidths.get(id)!;
    }
    const overRight = cellRect.right - (hostRect.right - rightLane - pad);
    const underLeft = hostRect.left + leftLane + pad - cellRect.left;
    const delta = overRight > 0 ? overRight : underLeft > 0 ? -underLeft : 0;
    if (delta === 0) return;
    scrollEl.scrollBy({
      left: delta,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, [visualLayout, resolvedWidths, scrollContainerRef]);

  // Per-column-id style objects (stable per layout) for header and body
  // cells: CSS-var width plus sticky pinned positioning. Pinned header cells
  // sit at the sticky-top ∩ sticky-left/right intersection → z-30; pinned
  // body cells → z-10.
  const { headerCellStyles, bodyCellStyles } = React.useMemo(() => {
    const header = new Map<string, React.CSSProperties>();
    const body = new Map<string, React.CSSProperties>();
    const leftSet = new Set(visualLayout.leftIds);
    const rightSet = new Set(visualLayout.rightIds);
    const lastLeftId = visualLayout.leftIds.at(-1);
    for (const id of visualLayout.visualIds) {
      const width = `var(${columnWidthVar(id)})`;
      const base: React.CSSProperties = {
        width,
        minWidth: width,
        maxWidth: width,
      };
      let headerStyle: React.CSSProperties = base;
      let bodyStyle: React.CSSProperties = base;
      if (leftSet.has(id)) {
        const left = `var(${columnLeftVar(id)})`;
        headerStyle = { ...base, position: "sticky", left, zIndex: 30 };
        bodyStyle = { ...base, position: "sticky", left, zIndex: 10 };
        // The lane's trailing header cell dissolves at its own edge, so a
        // column label panning underneath ghosts out instead of being cut.
        if (id === lastLeftId) {
          headerStyle = { ...headerStyle, backgroundImage: HEADER_FADE_GRADIENT };
        }
      } else if (rightSet.has(id)) {
        const right = `var(${columnRightVar(id)})`;
        // The lane's leading header cell fades too, but as a pseudo-element
        // class (HEADER_FADE_RIGHT_CLASS), not a style — see that constant.
        headerStyle = { ...base, position: "sticky", right, zIndex: 30 };
        bodyStyle = { ...base, position: "sticky", right, zIndex: 10 };
      } else {
        // Unpinned header cells are the container's x-snap areas — inert
        // until a narrow container arms scroll-snap-type (see the scroll
        // container's class), so wide layouts pay nothing for it.
        headerStyle = { ...base, scrollSnapAlign: "start" };
      }
      header.set(id, headerStyle);
      body.set(id, bodyStyle);
    }
    return { headerCellStyles: header, bodyCellStyles: body };
  }, [visualLayout]);

  const reorderableIdSet = React.useMemo(
    () => new Set(visualLayout.reorderableIds),
    [visualLayout],
  );

  // -------------------------------------------------------------------------
  // Resize + reorder drag controllers
  // -------------------------------------------------------------------------

  // Per-column drag bounds for the resize controller — meta min/max plus the
  // container-relative cap, resolved once per layout/measure.
  const widthLimits = React.useMemo(() => {
    const map = new Map<string, { min: number; max: number }>();
    for (const col of visualLayout.resolvable) {
      map.set(col.id, resolveColumnWidthLimits(col, containerWidth));
    }
    return map;
  }, [visualLayout, containerWidth]);

  const { controller: resizeController, activeResizeColumnId } =
    useColumnResize({
      scrollContainerRef,
      widths: resolvedWidths,
      pinnedLeftIds: visualLayout.leftIds,
      sizing,
      fillColumn,
      packed: columnLayout === "packed",
      widthLimits,
      onCommit: handleSizingChange,
    });

  const handleColumnDrop = React.useCallback(
    (draggedId: string, beforeId: string | null) => {
      // Reorder within the MERGED order (not the raw persisted one) so the
      // committed value always carries every current column — the same shape
      // the settings panel commits.
      const without = mergedOrder.filter((id) => id !== draggedId);
      let insertAt: number;
      if (beforeId !== null) {
        const idx = without.indexOf(beforeId);
        insertAt = idx === -1 ? without.length : idx;
      } else {
        // Dropped past the last visible column — land after it so trailing
        // hidden ids keep their stored slots. A drag needs two reorderable
        // columns, so there is a last one.
        const lastVisible = visualLayout.reorderableIds.at(-1)!;
        const idx = without.indexOf(lastVisible);
        insertAt = idx === -1 ? without.length : idx + 1;
      }
      const next = [
        ...without.slice(0, insertAt),
        draggedId,
        ...without.slice(insertAt),
      ];
      if (next.every((id, i) => id === mergedOrder[i])) return;
      handleOrderChange(next);
    },
    [mergedOrder, visualLayout, handleOrderChange],
  );

  const { draggingColumnId, dropIndicatorX, didDragRef, setGhostEl } =
    useColumnReorder({
      scrollContainerRef,
      enabled: !isTouch && visualLayout.reorderableIds.length > 1,
      visualIds: visualLayout.visualIds,
      reorderableIds: visualLayout.reorderableIds,
      widths: resolvedWidths,
      onDrop: handleColumnDrop,
    });

  const draggingColumnLabel = React.useMemo(() => {
    if (draggingColumnId === null) return null;
    // Only a column of the table's own is reorderable.
    const def = columns.find((d) => getColumnDefId(d) === draggingColumnId)!;
    return getColumnLabel(def, draggingColumnId);
  }, [draggingColumnId, columns]);

  // Content-coordinate span of the dragged column — drives the dimming veil
  // over its body cells (an overlay instead of per-cell opacity so no row
  // re-renders on drag start).
  const draggingColumnRect = React.useMemo(() => {
    if (draggingColumnId === null) return null;
    let left = 0;
    for (const id of visualLayout.visualIds) {
      const width = resolvedWidths.get(id)!;
      if (id === draggingColumnId) return { left, width };
      left += width;
    }
    return null;
  }, [draggingColumnId, visualLayout.visualIds, resolvedWidths]);

  const ghostRefCallback = React.useCallback(
    (node: HTMLDivElement | null) => setGhostEl(node),
    [setGhostEl],
  );

  return {
    visibility,
    tableColumnOrder,
    columnPinning,
    mergedOrder,
    visualLayout,
    resolvedWidths,
    totalResolvedWidth,
    headerCellStyles,
    bodyCellStyles,
    reorderableIdSet,
    resizeController,
    activeResizeColumnId,
    draggingColumnId,
    dropIndicatorX,
    didDragRef,
    ghostRefCallback,
    draggingColumnLabel,
    draggingColumnRect,
    handleColumnDrop,
  };
}
